import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Plus, Search, Package, BookOpen, History, ExternalLink, TrendingUp, TrendingDown, Upload } from "lucide-react";
import apiClient from "@/api/apiClient";
import { ENDPOINTS } from "@/api/endpoints";
import { showSuccess, showError } from "@/utils/toastHelpers";

import StockOverview from "../components/inventory/StockOverview";
import StockList from "../components/inventory/StockList";
import StockForm from "../components/inventory/StockForm";
import TransactionForm from "../components/inventory/TransactionForm";
import AlertsModal from "../components/inventory/AlertsModal";
import MedicineCatalog from "../components/inventory/MedicineCatalog";
import ReceiveStockForm from "../components/inventory/ReceiveStockForm";
import BulkStockUpload from "../components/inventory/BulkStockUpload";

export default function Inventory() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("stock");

  const [showStockForm, setShowStockForm] = useState(false);
  const [showReceiveStockForm, setShowReceiveStockForm] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [showTransactionForm, setShowTransactionForm] = useState(false);
  const [showAlertsModal, setShowAlertsModal] = useState(false);
  const [alertsModalType, setAlertsModalType] = useState(null); // 'lowStock' or 'expiring'
  const [editingItem, setEditingItem] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [transactionTypeFilter, setTransactionTypeFilter] = useState("all");
  const [transactionSearchTerm, setTransactionSearchTerm] = useState("");
  const [dateError, setDateError] = useState("");

  // Fetch inventory items using React Query
  const {
    data: stockItems = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["inventory", categoryFilter, statusFilter],
    queryFn: () => {
      const params = new URLSearchParams();
      if (categoryFilter !== "all") {
        params.append("category", categoryFilter);
      }
      if (statusFilter !== "all") {
        params.append("status", statusFilter);
      }
      return apiClient.get(`${ENDPOINTS.INVENTORY.LIST}?${params.toString()}`);
    },
  });

  // Fetch inventory transactions
  const { data: transactions = [], isLoading: isLoadingTransactions, error: transactionsError } = useQuery({
    queryKey: ["inventoryTransactions"],
    queryFn: () => apiClient.get(ENDPOINTS.INVENTORY.TRANSACTIONS),
  });

  // Fetch low stock alerts
  const { data: lowStockItems = [] } = useQuery({
    queryKey: ["lowStockAlerts"],
    queryFn: () => apiClient.get(ENDPOINTS.INVENTORY.LOW_STOCK),
  });

  // Fetch expiring items
  const { data: expiringItems = [] } = useQuery({
    queryKey: ["expiringItems"],
    queryFn: () => apiClient.get(ENDPOINTS.INVENTORY.EXPIRING),
  });

  // Create inventory item mutation
  const createInventoryItem = useMutation({
    mutationFn: (data) => apiClient.post(ENDPOINTS.INVENTORY.CREATE, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["inventory"]);
      queryClient.invalidateQueries(["inventoryTransactions"]);
      setShowStockForm(false);
      setEditingItem(null);
      showSuccess("Inventory item created successfully");
    },
    onError: (error) => {
      console.error("Failed to create inventory item:", error);
      showError(
        "Failed to create inventory item",
        error.response?.data?.error || "Please try again"
      );
    },
  });

  // Update inventory item mutation
  const updateInventoryItem = useMutation({
    mutationFn: ({ id, data }) =>
      apiClient.put(ENDPOINTS.INVENTORY.UPDATE(id), data),
    onSuccess: () => {
      queryClient.invalidateQueries(["inventory"]);
      setShowStockForm(false);
      setEditingItem(null);
      showSuccess("Inventory item updated successfully");
    },
    onError: (error) => {
      console.error("Failed to update inventory item:", error);
      showError(
        "Failed to update inventory item",
        error.response?.data?.error || "Please try again"
      );
    },
  });

  // Delete inventory item mutation
  const deleteInventoryItem = useMutation({
    mutationFn: (id) => apiClient.delete(ENDPOINTS.INVENTORY.DELETE(id)),
    onSuccess: () => {
      queryClient.invalidateQueries(["inventory"]);
      queryClient.invalidateQueries(["inventoryTransactions"]);
      showSuccess("Inventory item deleted successfully");
    },
    onError: (error) => {
      console.error("Failed to delete inventory item:", error);
      showError(
        "Failed to delete inventory item",
        error.response?.data?.error || "Please try again"
      );
    },
  });

  // Create inventory transaction mutation
  const createTransaction = useMutation({
    mutationFn: (data) =>
      apiClient.post(ENDPOINTS.INVENTORY.TRANSACTIONS, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["inventory"]);
      queryClient.invalidateQueries(["inventoryTransactions"]);
      queryClient.invalidateQueries(["lowStockAlerts"]);
      setShowTransactionForm(false);
      setSelectedItem(null);
      showSuccess("Transaction processed successfully");
    },
    onError: (error) => {
      console.error("Failed to create transaction:", error);
      showError(
        "Failed to process transaction",
        error.response?.data?.error || "Please try again"
      );
    },
  });

  const handleAddItem = () => {
    setEditingItem(null);
    setShowStockForm(true);
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setShowStockForm(true);
  };

  const handleSaveItem = async (data) => {
    try {
      if (editingItem) {
        await updateInventoryItem.mutateAsync({ id: editingItem.id, data });
      } else {
        await createInventoryItem.mutateAsync(data);
      }
    } catch (error) {
      // Error handling is done in the mutation's onError callback
    }
  };

  const handleTransaction = (item) => {
    setSelectedItem(item);
    setShowTransactionForm(true);
  };

  const handleLowStockClick = () => {
    setAlertsModalType("lowStock");
    setShowAlertsModal(true);
  };

  const handleExpiringClick = () => {
    setAlertsModalType("expiring");
    setShowAlertsModal(true);
  };

  const handleSaveTransaction = async (data) => {
    try {
      await createTransaction.mutateAsync(data);
    } catch (error) {
      // Error handling is done in the mutation's onError callback
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (
      window.confirm(
        "Are you sure you want to delete this stock item? This action will also remove related transaction history and cannot be undone."
      )
    ) {
      try {
        await deleteInventoryItem.mutateAsync(itemId);
      } catch (error) {
        // Error handling is done in the mutation's onError callback
      }
    }
  };

  const handleStartDateChange = (value) => {
    setStartDate(value);
    // Validate if end date exists and is before start date
    if (endDate && value && new Date(value) > new Date(endDate)) {
      setDateError("Start date cannot be after end date");
    } else {
      setDateError("");
    }
  };

  const handleEndDateChange = (value) => {
    setEndDate(value);
    // Validate if start date exists and end date is before start date
    if (startDate && value && new Date(value) < new Date(startDate)) {
      setDateError("End date cannot be before start date");
    } else {
      setDateError("");
    }
  };

  const handleClearFilters = () => {
    setStartDate("");
    setEndDate("");
    setTransactionTypeFilter("all");
    setTransactionSearchTerm("");
    setDateError("");
  };

  const filteredItems = stockItems.filter((item) => {
    const searchMatch =
      item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const categoryMatch =
      categoryFilter === "all" || item.category === categoryFilter;
    const statusMatch = statusFilter === "all" || (item.statuses || []).includes(statusFilter);
    return searchMatch && categoryMatch && statusMatch;
  });

  const tabs = [
    { id: "stock", label: "Stock Inventory", icon: Package },
    { id: "catalog", label: "Medicine Catalog", icon: BookOpen },
    { id: "history", label: "Transaction History", icon: History },
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">
            Inventory Management
          </h1>
          <p className="text-slate-600">Manage medicine catalog and stock</p>
        </div>
        {activeTab === "stock" && (
          <div className="flex gap-3">
            <Button
              onClick={() => setShowBulkUpload(true)}
              variant="outline"
              className="border-blue-300 text-blue-700 hover:bg-blue-50"
              title="Upload CSV or Excel file"
            >
              <Upload className="w-4 h-4 mr-2" />
              Bulk Upload
            </Button>
            <Button
              onClick={() => setShowReceiveStockForm(true)}
              variant="outline"
              className="border-green-300 text-green-700 hover:bg-green-50"
            >
              <Plus className="w-4 h-4 mr-2" />
              Receive Stock
            </Button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors
                  ${
                    activeTab === tab.id
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
                  }
                `}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === "stock" && (
        <>
          {/* Overview Stats */}
          <StockOverview
            stockItems={stockItems}
            transactions={transactions}
            lowStockCount={lowStockItems.length}
            expiringCount={expiringItems.length}
            onLowStockClick={handleLowStockClick}
            onExpiringClick={handleExpiringClick}
          />

          {/* Search and Filters */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input
                placeholder="Search items by medicine name or batch..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-white/60 backdrop-blur-sm border-slate-200/60"
              />
            </div>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-md bg-white/60 backdrop-blur-sm"
            >
              <option value="all">All Status</option>
              <option value="in_stock">In Stock</option>
              <option value="low_stock">Low Stock</option>
              <option value="out_of_stock">Out of Stock</option>
              <option value="expiring_soon">Expiring Soon</option>
              <option value="expired">Expired</option>
            </select>
          </div>

          {/* Stock List */}
          <StockList
            items={filteredItems}
            isLoading={isLoading}
            onEditItem={handleEditItem}
            onTransaction={handleTransaction}
            onDeleteItem={handleDeleteItem}
          />
        </>
      )}

      {activeTab === "catalog" && <MedicineCatalog />}

      {activeTab === "history" && (
        <div className="space-y-6">
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
            <Input
              placeholder="Search by medicine name or prescription reference..."
              value={transactionSearchTerm}
              onChange={(e) => setTransactionSearchTerm(e.target.value)}
              className="pl-10 bg-white/60 backdrop-blur-sm border-slate-200/60"
            />
          </div>

          {/* Date Range and Type Filters */}
          <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-sm border border-slate-200/60 p-4">
            <div className="flex flex-col md:flex-row gap-4 items-end">
              <div className="flex-1">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Start Date
                </label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => handleStartDateChange(e.target.value)}
                  className={`bg-white/60 backdrop-blur-sm ${
                    dateError ? "border-red-300" : "border-slate-200/60"
                  }`}
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  End Date
                </label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => handleEndDateChange(e.target.value)}
                  className={`bg-white/60 backdrop-blur-sm ${
                    dateError ? "border-red-300" : "border-slate-200/60"
                  }`}
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Transaction Type
                </label>
                <select
                  value={transactionTypeFilter}
                  onChange={(e) => setTransactionTypeFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-md bg-white/60 backdrop-blur-sm"
                >
                  <option value="all">All Types</option>
                  <option value="receive">Incoming Stock</option>
                  <option value="dispense">Outgoing Stock</option>
                  <option value="adjustment">Adjustment</option>
                </select>
              </div>
              <Button
                onClick={handleClearFilters}
                variant="outline"
                className="border-slate-300"
              >
                Clear Filters
              </Button>
            </div>
            {dateError && (
              <p className="text-sm text-red-600 mt-2">{dateError}</p>
            )}
          </div>

          {/* Transaction Overview */}
          {(() => {
            // Calculate totals from filtered transactions
            const filteredForTotals = transactions.filter((transaction) => {
              if (!transaction.createdAt) return false;

              // Search filtering (by medicine name or prescription reference)
              if (transactionSearchTerm) {
                const searchLower = transactionSearchTerm.toLowerCase();
                const item = stockItems.find((i) => i.id === transaction.itemId);
                const medicineName = item?.name?.toLowerCase() || "";
                const prescriptionRef = transaction.prescriptionId?.toLowerCase() || "";
                const referenceNumber = transaction.referenceNumber?.toLowerCase() || "";

                const matchesSearch =
                  medicineName.includes(searchLower) ||
                  prescriptionRef.includes(searchLower) ||
                  referenceNumber.includes(searchLower);

                if (!matchesSearch) return false;
              }

              // Date filtering
              const transDate = new Date(transaction.createdAt);
              transDate.setHours(0, 0, 0, 0);

              if (startDate) {
                const start = new Date(startDate);
                start.setHours(0, 0, 0, 0);
                if (transDate < start) return false;
              }

              if (endDate) {
                const end = new Date(endDate);
                end.setHours(23, 59, 59, 999);
                if (transDate > end) return false;
              }

              // Transaction type filtering
              if (transactionTypeFilter !== "all" && transaction.transactionType !== transactionTypeFilter) {
                return false;
              }

              return true;
            });

            // Total Incoming: receive + positive adjustments
            const totalIncoming = filteredForTotals.reduce((sum, t) => {
              const qty = Number(t.quantity) || 0;
              if (t.transactionType === "receive") {
                return sum + qty;
              }
              if (t.transactionType === "adjustment" && qty > 0) {
                return sum + qty;
              }
              return sum;
            }, 0);

            // Total Outgoing: dispense + absolute value of negative adjustments
            const totalOutgoing = filteredForTotals.reduce((sum, t) => {
              const qty = Number(t.quantity) || 0;
              if (t.transactionType === "dispense") {
                return sum + qty;
              }
              if (t.transactionType === "adjustment" && qty < 0) {
                return sum + Math.abs(qty);
              }
              return sum;
            }, 0);

            const netChange = totalIncoming - totalOutgoing;

            return (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="bg-white/60 backdrop-blur-sm border-slate-200/60 shadow-lg hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-slate-600">
                          Total Incoming
                        </p>
                        <p className="text-3xl font-bold text-blue-600">
                          +{totalIncoming}
                        </p>
                        <p className="text-sm text-slate-500">
                          Received & adjustments up
                        </p>
                      </div>
                      <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg">
                        <TrendingUp className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white/60 backdrop-blur-sm border-slate-200/60 shadow-lg hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-slate-600">
                          Total Outgoing
                        </p>
                        <p className="text-3xl font-bold text-red-600">
                          -{totalOutgoing}
                        </p>
                        <p className="text-sm text-slate-500">
                          Dispensed & adjustments down
                        </p>
                      </div>
                      <div className="p-3 rounded-xl bg-gradient-to-br from-red-500 to-red-600 shadow-lg">
                        <TrendingDown className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white/60 backdrop-blur-sm border-slate-200/60 shadow-lg hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-slate-600">
                          Net Change
                        </p>
                        <p className={`text-3xl font-bold ${
                          netChange > 0
                            ? "text-green-600"
                            : netChange < 0
                              ? "text-orange-600"
                              : "text-slate-600"
                        }`}>
                          {netChange > 0 ? "+" : ""}{netChange}
                        </p>
                        <p className="text-sm text-slate-500">
                          Overall balance
                        </p>
                      </div>
                      <div className={`p-3 rounded-xl bg-gradient-to-br shadow-lg ${
                        netChange > 0
                          ? "from-green-500 to-green-600"
                          : netChange < 0
                            ? "from-orange-500 to-orange-600"
                            : "from-slate-500 to-slate-600"
                      }`}>
                        <Package className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            );
          })()}

          {/* Transaction Table */}
          <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-sm border border-slate-200/60 p-6">
            <h2 className="text-xl font-bold text-slate-900 mb-4">
              Transaction History
            </h2>
            {isLoadingTransactions ? (
              <p className="text-slate-600 text-center py-8">
                Loading transactions...
              </p>
            ) : transactionsError ? (
              <p className="text-red-600 text-center py-8">
                Error loading transactions: {transactionsError.message}
              </p>
            ) : (() => {
                // Filter transactions by search, date range, and type
                const filteredTransactions = transactions.filter((transaction) => {
                  if (!transaction.createdAt) return false;

                  // Search filtering (by medicine name or prescription reference)
                  if (transactionSearchTerm) {
                    const searchLower = transactionSearchTerm.toLowerCase();
                    const item = stockItems.find((i) => i.id === transaction.itemId);
                    const medicineName = item?.name?.toLowerCase() || "";
                    const prescriptionRef = transaction.prescriptionId?.toLowerCase() || "";
                    const referenceNumber = transaction.referenceNumber?.toLowerCase() || "";

                    const matchesSearch =
                      medicineName.includes(searchLower) ||
                      prescriptionRef.includes(searchLower) ||
                      referenceNumber.includes(searchLower);

                    if (!matchesSearch) return false;
                  }

                  // Date filtering
                  const transDate = new Date(transaction.createdAt);
                  transDate.setHours(0, 0, 0, 0); // Reset time for date-only comparison

                  if (startDate) {
                    const start = new Date(startDate);
                    start.setHours(0, 0, 0, 0);
                    if (transDate < start) return false;
                  }

                  if (endDate) {
                    const end = new Date(endDate);
                    end.setHours(23, 59, 59, 999);
                    if (transDate > end) return false;
                  }

                  // Transaction type filtering
                  if (transactionTypeFilter !== "all" && transaction.transactionType !== transactionTypeFilter) {
                    return false;
                  }

                  return true;
                });

                return filteredTransactions.length === 0 ? (
                  <p className="text-slate-600 text-center py-8">
                    {startDate || endDate
                      ? "No transactions found in this date range"
                      : "No transactions yet"}
                  </p>
                ) : (
                  <>
                    <div className="mb-4 text-sm text-slate-600">
                      Showing {filteredTransactions.length} of {transactions.length} transaction
                      {filteredTransactions.length !== 1 ? "s" : ""}
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-slate-50 border-b border-slate-200">
                          <tr>
                            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">
                              Date
                            </th>
                            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">
                              Type
                            </th>
                            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">
                              Medicine/Item
                            </th>
                            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">
                              Quantity
                            </th>
                            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">
                              Reason
                            </th>
                            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">
                              Reference
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200">
                          {filteredTransactions.map((transaction) => (
                            <tr key={transaction.id} className="hover:bg-slate-50">
                              <td className="px-4 py-3 text-sm text-slate-600">
                                {transaction.createdAt
                                  ? new Date(transaction.createdAt).toLocaleDateString()
                                  : "-"}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                <span
                                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    transaction.transactionType === "receive"
                                      ? "bg-green-100 text-green-800"
                                      : transaction.transactionType === "dispense"
                                        ? "bg-blue-100 text-blue-800"
                                        : "bg-slate-100 text-slate-800"
                                  }`}
                                >
                                  {transaction.transactionType}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-slate-900">
                                {(() => {
                                  const item = stockItems.find(
                                    (i) => i.id === transaction.itemId
                                  );
                                  return item?.name || `Item #${transaction.itemId?.substring(0, 8)}`;
                                })()}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                <span
                                  className={`font-semibold ${
                                    transaction.transactionType === "receive"
                                      ? "text-blue-600"
                                      : transaction.transactionType === "adjustment"
                                      ? transaction.quantity >= 0
                                        ? "text-green-600"
                                        : "text-red-600"
                                      : "text-red-600"
                                  }`}
                                >
                                  {transaction.transactionType === "receive"
                                    ? `+${transaction.quantity}`
                                    : transaction.transactionType === "adjustment"
                                    ? transaction.quantity >= 0
                                      ? `+${transaction.quantity}`
                                      : transaction.quantity
                                    : `-${transaction.quantity}`}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-slate-600">
                                {transaction.reason || "-"}
                              </td>
                              <td className="px-4 py-3 text-sm text-slate-600">
                                {transaction.prescriptionId ? (
                                  <button
                                    onClick={() => navigate(`/Prescriptions?prescriptionId=${transaction.prescriptionId}`)}
                                    className="flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline"
                                  >
                                    {transaction.referenceNumber || transaction.prescriptionId.substring(0, 8)}
                                    <ExternalLink className="w-3 h-3" />
                                  </button>
                                ) : (
                                  transaction.referenceNumber || "-"
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                );
              })()}
          </div>
        </div>
      )}

      {/* Forms */}
      {showStockForm && (
        <StockForm
          item={editingItem}
          onSave={handleSaveItem}
          onClose={() => setShowStockForm(false)}
        />
      )}

      {showReceiveStockForm && (
        <ReceiveStockForm onClose={() => setShowReceiveStockForm(false)} />
      )}

      {showBulkUpload && (
        <BulkStockUpload onClose={() => setShowBulkUpload(false)} />
      )}

      {showTransactionForm && (
        <TransactionForm
          item={selectedItem}
          onSave={handleSaveTransaction}
          onClose={() => setShowTransactionForm(false)}
        />
      )}

      {showAlertsModal && (
        <AlertsModal
          isOpen={showAlertsModal}
          onClose={() => setShowAlertsModal(false)}
          type={alertsModalType}
          items={alertsModalType === "lowStock" ? lowStockItems : expiringItems}
          onReorder={handleTransaction}
        />
      )}
    </div>
  );
}
